/**
 * SubscriptionGate/index.js
 *
 * Fixed full-screen overlay that blocks POS access when the store's
 * subscription is not active.
 *
 * Three rendering phases:
 *   1. !isLoaded                        → null (transparent; don't block while seeding)
 *   2. isLoaded && !isLiveChecked && isGated  → "Checking subscription…" spinner card
 *   3. isLiveChecked && isGated         → hard gate (shield, countdown, CTAs)
 *
 * fetchFailed scenario: the SubscriptionContext sets isLoaded=true but
 * isLiveChecked=false on network error.  Because isGated requires an
 * ACTIVE subscription check, any uncertainty resolves to optimistic
 * pass-through — the gate only hard-blocks when we have a confirmed
 * live result showing the subscription is not active.
 *
 * Whitelist: routes whose path segment matches one of these strings are
 * always allowed through (account & settings screens must remain reachable
 * so staff can view/renew, and login/location are always public).
 */

import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSubscription } from "../../context/SubscriptionContext";
import "./index.css";

/* Routes whose pathname contains one of these segments are whitelisted */
const WHITELIST = new Set(["account", "settings", "location", "signIn", "profile", "dual-screen"]);

function isWhitelisted(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  return segments.some((seg) => WHITELIST.has(seg));
}

/* Statuses that allow POS access (TRIAL_ACTIVE behaves like ACTIVE) */
const ALLOWED_STATUSES = new Set(["ACTIVE", "TRIAL_ACTIVE"]);

/**
 * Derive whether the subscription bundle indicates a gate should fire.
 * Returns true only when we have a confirmed bundle with a non-allowed
 * (or absent) subscription record.
 */
function computeIsGated(bundle) {
  if (!bundle) return false; // no data → optimistic pass
  // If there's a subscription record, gate on non-allowed status
  if (bundle.subscription) {
    return !ALLOWED_STATUSES.has(bundle.subscription.status);
  }
  // If subscriptionMessage explicitly signals inactive
  if (bundle.subscriptionMessage && bundle.subscriptionMessage !== "Success") {
    return true;
  }
  return false;
}

/* ── 30-second countdown hook ──────────────────────────── */
function useCountdown(seconds, onDone) {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    setRemaining(seconds);
    const id = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          onDone();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [seconds, onDone]); // eslint-disable-line react-hooks/exhaustive-deps
  return remaining;
}

/* ── Shield icon SVG ────────────────────────────────────── */
function ShieldIcon() {
  return (
    <svg
      className="sg-shield-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7L12 2z" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

/* ── Spinner SVG ────────────────────────────────────────── */
function SpinnerIcon() {
  return (
    <svg
      className="sg-spinner"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <circle cx="12" cy="12" r="10" opacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

/* ── Hard-gate card ─────────────────────────────────────── */
function HardGateCard({ bundle, onRetry }) {
  const navigate = useNavigate();
  const status = bundle?.subscription?.status || "UNKNOWN";
  const planName =
    bundle?.subscription?.packageName || bundle?.upcomingBilling?.packageName || null;

  const handleRetry = useCallback(() => {
    onRetry();
  }, [onRetry]);

  const remaining = useCountdown(30, handleRetry);

  return (
    <div className="sg-card sg-card--hard">
      <ShieldIcon />

      <div className="sg-status-badge">
        {status === "EXPIRED"
          ? "Subscription Expired"
          : status === "SUSPENDED"
          ? "Subscription Suspended"
          : "Subscription Inactive"}
      </div>

      {planName && <p className="sg-plan-name">{planName}</p>}

      <h2 className="sg-title">POS Access Restricted</h2>
      <p className="sg-body">
        Your store&apos;s subscription is no longer active. Renew your plan to continue using the
        Point of Sale system.
      </p>

      <div className="sg-cta-group">
        <button
          className="sg-btn sg-btn--primary"
          onClick={() => navigate("/settings/account-subscription")}
        >
          View Account &amp; Subscription
        </button>
        <button className="sg-btn sg-btn--secondary" onClick={() => navigate("/products/location")}>
          Switch Location
        </button>
      </div>

      <p className="sg-retry-hint">
        Retrying in <strong>{remaining}s</strong>&hellip;
      </p>
    </div>
  );
}

/* ── Checking card ──────────────────────────────────────── */
function CheckingCard() {
  return (
    <div className="sg-card sg-card--checking">
      <SpinnerIcon />
      <p className="sg-checking-text">Checking subscription&hellip;</p>
    </div>
  );
}

/* ── Main overlay component ─────────────────────────────── */
export default function SubscriptionGateOverlay() {
  const { bundle, isLoaded, isLiveChecked, refresh } = useSubscription();
  const { pathname } = useLocation();

  // Whitelist: never block these routes
  if (isWhitelisted(pathname)) return null;

  // Phase 1: Not yet seeded from cache or fetch — transparent
  if (!isLoaded) return null;

  const isGated = computeIsGated(bundle);

  // Phase 2: We have cached data suggesting inactive, but live check still running
  if (isLoaded && !isLiveChecked && isGated) {
    return (
      <div className="sg-overlay">
        <CheckingCard />
      </div>
    );
  }

  // Phase 3: Live check confirmed — hard gate
  if (isLiveChecked && isGated) {
    return (
      <div className="sg-overlay">
        <HardGateCard bundle={bundle} onRetry={refresh} />
      </div>
    );
  }

  // Subscription is valid (or fetch failed → optimistic pass-through)
  return null;
}
